// Copyright (c) 2025 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import * as Slice from "../slices/timeline"
import * as Actions from "../actions"
import * as TimesheetFieldsActions from "../../timesheet-fields/actions"
import { grpc } from "../../../app/rpc/executor"
import { showSnackBar } from "../../snackbar/actions"
import { timesheetFieldClient } from "../../../app/rpc/client"
import {
  ListTimesheetFieldOptionsRequest,
  ListTimesheetFieldsRequest,
  ListTimesheetFieldsResponse,
} from "../../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"

type ListTimesheetFieldsRpc = (request: ListTimesheetFieldsRequest) => Observable<ListTimesheetFieldsResponse>

export const listTimesheetFields = (listTimesheetFieldsRpc: ListTimesheetFieldsRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.listTimesheetFields.match),
      mergeMap(action => of(action.payload.request)
        .pipe(
          mergeMap(listTimesheetFieldsRpc),
          map(t => t.toObject().timesheetFieldsList),
          mergeMap(result => {
            const actions: Action[] = [
              Slice.setTimesheetFields({
                timesheetFields: result,
                siteId: action.payload.request.getSiteId(),
              }),
            ]
            if (action.payload.includeOptions && result.length) {
              result.forEach(timesheetField => {
                actions.push(TimesheetFieldsActions.listTimesheetFieldOptions(
                  new ListTimesheetFieldOptionsRequest()
                    .setTimesheetFieldId(timesheetField.id)
                    .setSiteId(timesheetField.siteId)
                ))
              })
            }
            return concat(actions)
          }),
          catchError(() => of(showSnackBar({
            message: t({
              id: "msg_list_timesheet_fields_failed",
              message: "Failed to load timesheet fields. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
            }),
            messageType: "ERROR",
          }))),
        )
      ),
    )

const listTimesheetFieldsRpc = (request: ListTimesheetFieldsRequest): Observable<ListTimesheetFieldsResponse> =>
  grpc(() => timesheetFieldClient.listTimesheetFields(request, null))

export default listTimesheetFields(listTimesheetFieldsRpc)
